import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

const ResourceModal = ({ isOpen, onClose, onSave, resource, loading }) => {
    const [formData, setFormData] = useState({
        title: '',
        category: 'Product Brochures',
        customCategory: '',
        language: 'None',
        display_order: 0,
        is_active: true
    });
    const [file, setFile] = useState(null);
    const [useCustomCategory, setUseCustomCategory] = useState(false);

    useEffect(() => {
        if (resource) {
            const hasPredefinedCategory = ['Product Brochures', 'Customer Checklists'].includes(resource.category);
            setFormData({
                title: resource.title || '',
                category: hasPredefinedCategory ? resource.category : 'Custom',
                customCategory: hasPredefinedCategory ? '' : resource.category,
                language: resource.language || 'None',
                display_order: resource.display_order || 0,
                is_active: resource.is_active === 1 || resource.is_active === true
            });
            setUseCustomCategory(!hasPredefinedCategory);
            setFile(null);
        } else {
            setFormData({
                title: '',
                category: 'Product Brochures',
                customCategory: '',
                language: 'None',
                display_order: 0,
                is_active: true
            });
            setUseCustomCategory(false);
            setFile(null);
        }
    }, [resource, isOpen]);

    if (!isOpen) return null;

    const handleCategoryChange = (e) => {
        const val = e.target.value;
        if (val === 'Custom') {
            setUseCustomCategory(true);
            setFormData({ ...formData, category: 'Custom' });
        } else {
            setUseCustomCategory(false);
            setFormData({ ...formData, category: val });
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Validation
        const finalCategory = useCustomCategory ? formData.customCategory : formData.category;
        if (!finalCategory || finalCategory.trim() === '') {
            toast.error('Please select or specify a category');
            return;
        }

        if (file) {
            const allowedExtensions = ['pdf', 'doc', 'docx'];
            const fileExtension = file.name.split('.').pop().toLowerCase();
            if (!allowedExtensions.includes(fileExtension)) {
                toast.error('Only PDF and Word files are allowed.');
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                toast.error('File size exceeds 5MB limit. Please select a smaller file.');
                return;
            }
        }

        const data = new FormData();
        data.append('title', formData.title);
        data.append('category', finalCategory);
        data.append('language', formData.language === 'None' ? '' : formData.language);
        data.append('display_order', formData.display_order);
        data.append('is_active', formData.is_active ? 1 : 0);
        if (file) {
            data.append('file', file);
        }

        onSave(data);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                {/* Header */}
                <div className="flex items-center justify-between px-8 py-6 border-b border-slate-100">
                    <h2 className="text-2xl font-bold text-slate-800">
                        {resource ? 'Edit Resource Document' : 'Add Resource Document'}
                    </h2>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                        <XMarkIcon className="h-6 w-6 text-slate-500" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-8 space-y-5">
                    {/* Title */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Document Title
                        </label>
                        <input
                            type="text"
                            required
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                            placeholder="e.g. 5/20/30/40 method brochure"
                        />
                    </div>

                    {/* Category Select */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Category
                            </label>
                            <select
                                value={useCustomCategory ? 'Custom' : formData.category}
                                onChange={handleCategoryChange}
                                className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none bg-white transition-all"
                            >
                                <option value="Product Brochures">Product Brochures</option>
                                <option value="Customer Checklists">Customer Checklists</option>
                                <option value="Custom">Custom (Specify...)</option>
                            </select>
                        </div>

                        {/* Custom Category Input */}
                        {useCustomCategory && (
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    Specify Custom Category
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={formData.customCategory}
                                    onChange={(e) => setFormData({ ...formData, customCategory: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                                    placeholder="Enter custom category name"
                                />
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {/* Language Select */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Language
                            </label>
                            <select
                                value={formData.language}
                                onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                                className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none bg-white transition-all"
                            >
                                <option value="None">None / General</option>
                                <option value="Telugu">Telugu</option>
                                <option value="Tamil">Tamil</option>
                                <option value="Kannada">Kannada</option>
                                <option value="English">English</option>
                            </select>
                        </div>

                        {/* Display Order */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Display Order
                            </label>
                            <input
                                type="number"
                                required
                                value={formData.display_order}
                                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                                className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                            />
                        </div>
                    </div>

                    {/* File Upload */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Document File {resource ? '(Optional - upload to replace)' : '(Required)'}
                        </label>
                        <input
                            type="file"
                            required={!resource}
                            accept=".pdf,.doc,.docx"
                            onChange={(e) => {
                                const selectedFile = e.target.files[0];
                                if (selectedFile) {
                                    const allowedExtensions = ['pdf', 'doc', 'docx'];
                                    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
                                    if (!allowedExtensions.includes(fileExtension)) {
                                        toast.error('Only PDF and Word files are allowed.');
                                        e.target.value = null;
                                        setFile(null);
                                        return;
                                    }
                                    if (selectedFile.size > 5 * 1024 * 1024) {
                                        toast.error('File size exceeds 5MB limit. Please select a smaller file.');
                                        e.target.value = null;
                                        setFile(null);
                                        return;
                                    }
                                }
                                setFile(selectedFile);
                            }}
                            className="w-full text-slate-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 cursor-pointer"
                        />
                        <p className="text-slate-400 text-xs mt-1">
                            Accepted formats: PDF, Word (Max 5MB)
                        </p>
                    </div>

                    {/* Active Status */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Status
                        </label>
                        <button
                            type="button"
                            onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                            className={`w-full flex items-center justify-between px-6 py-3 rounded-2xl border transition-all ${
                                formData.is_active
                                    ? 'bg-green-50 border-green-200 text-green-700'
                                    : 'bg-slate-50 border-slate-200 text-slate-500'
                            }`}
                        >
                            <span className="font-semibold">{formData.is_active ? 'Active' : 'Inactive'}</span>
                            <div className={`w-10 h-6 rounded-full relative transition-all ${formData.is_active ? 'bg-green-500' : 'bg-slate-300'}`}>
                                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${formData.is_active ? 'left-5' : 'left-1'}`}></div>
                            </div>
                        </button>
                    </div>
                </form>

                {/* Footer Actions */}
                <div className="px-8 py-6 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-3 rounded-xl font-semibold text-slate-600 hover:bg-slate-200 transition-all"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-xl font-semibold transition-all disabled:opacity-50 shadow-lg"
                    >
                        {loading ? 'Saving...' : resource ? 'Update Document' : 'Upload Document'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ResourceModal;
